// utils.tsx
import React from 'react';
import { Tooltip } from 'antd';
import IconButton from '~components/_common/IconButton/IconButton';
import { DbOrderVoucherOrGroup } from '~src/types/db';

/**
 * Icon Configuration based on Voucher Type
 */
export const getVoucherTypeConfig = (type: number) => {
  switch (type) {
    case 1: // SUM
      return {
        icon: 'hashtag',
        labelId: 'voucher-type-sum',
        descriptionId: 'voucher-type-sum-description',
        color: '#595959',
        iconStyle: 'fas',
      };
    case 2: // Offer list
      return {
        icon: 'file-alt',
        labelId: 'offer-list',
        descriptionId: 'voucher-type-offer-list-description',
        color: 'green',
        iconStyle: 'far',
      };
    case 3: // Certified document
      return {
        icon: 'file-alt',
        labelId: 'certified-document',
        descriptionId: 'voucher-type-cert-doc-description',
        color: 'red',
        iconStyle: 'far',
      };
    case 4: // Terms of Service
      return {
        icon: 'check-circle',
        labelId: 'terms-of-service',
        descriptionId: 'voucher-type-tos-description',
        color: 'orange',
        iconStyle: 'far',
      };
    default:
      return null;
  }
};

/**
 * Icon Render
 */
export const renderVoucherTypeIcon = (type: number, intl: any) => {
  const config = getVoucherTypeConfig(type);
  if (!config) return null;

  const tooltipTitle = intl.formatMessage({ id: config.descriptionId });

  // Custom color mapping to ensure CSS visibility
  const iconColor = config.color === 'orange' ? '#fa8c16' : config.color;

  return (
    <Tooltip title={tooltipTitle}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '24px',
          height: '24px',
          cursor: 'help',
        }}>
        <IconButton
          icon={config.icon}
          iconStyle={config.iconStyle || 'far'}
          color={config.color}
          backgroundOff
          customStyle={{
            fontSize: '14px',
            color: iconColor !== 'inherit' ? iconColor : undefined,
          }}
        />
      </span>
    </Tooltip>
  );
};

const sortVouchers = (
  restVouchersList: DbOrderVoucherOrGroup[],
  currentLevelVouchersList: DbOrderVoucherOrGroup[]
) => {
  for (const voucher of currentLevelVouchersList) {
    const currentVoucherContractId =
      voucher.voucher_group_option?.voucher_group_contract_id;

    const otherVouchers: DbOrderVoucherOrGroup[] = [];
    const nextLevelVouchers: DbOrderVoucherOrGroup[] = [];

    voucher.vouchers = restVouchersList
      .filter((voucher) => {
        if (voucher.is_voucher_group) nextLevelVouchers.push(voucher);

        if (voucher.contract_id === currentVoucherContractId) return true;

        otherVouchers.push(voucher);
        return false;
      })
      .sort((a, b) => {
        if (!a.sort && !b.sort) return a.created_at > b.created_at ? 1 : -1;
        if (b.sort) return 1;
        if (a.sort) return -1;

        return a.sort! - b.sort!;
      });

    if (!!nextLevelVouchers.length) {
      sortVouchers(otherVouchers, nextLevelVouchers);
    }
  }
};

export const getSortedVouchersList = (
  vouchersList: DbOrderVoucherOrGroup[],
  initialFolderContractId?: number
): DbOrderVoucherOrGroup[] => {
  const otherContractVouchers: DbOrderVoucherOrGroup[] = [];
  const nextIterationFoldersVoucher: DbOrderVoucherOrGroup[] = [];

  const currentContractVoucher: DbOrderVoucherOrGroup[] = vouchersList
    .filter((voucher) => {
      if (voucher.contract_id === initialFolderContractId) {
        if (voucher.is_voucher_group) {
          nextIterationFoldersVoucher.push(voucher);
        }

        return true;
      }

      otherContractVouchers.push(voucher);
      return false;
    })
    .sort(({ sort: a }, { sort: b }) => {
      if (a === null && b === null) return 0;
      if (a === null) return 1;
      if (b === null) return -1;
      return a - b;
    });

  sortVouchers(otherContractVouchers, nextIterationFoldersVoucher);

  return currentContractVoucher;
};

//
export const convertVouchersToContracts = (vouchers) => {
  const groups = [];
  const cards = [];
  const cardMap = {};

  for (const v of vouchers) {
    const baseCard = {
      id: String(v.id),
      type: 'card',
      subType: v.voucher_type ?? 1,
      cardTitle: v.name ?? 'Untitled',
      cardSubtitle: v.description ?? '',
      cardQuantity: v.unit_count ?? 0,
      cardPrice: 0,
      cardSum: 0,
      cardFiles: v.attachments ?? [],
      unitType: v.unit_type,
      sort: v.sort ?? 0,
      state: v.state,
      tenderVoucherId: v.tender_voucher_id,
    };

    if (v.is_voucher_group) {
      groups.push({
        id: String(v.id),
        type: 'group',
        groupTitle: v.name ?? 'Unnamed Group',
        cards: [],
        subVoucherIds: v.voucher_group_option?.sub_voucher_ids ?? [],
        sort: v.sort ?? 0,
      });
    } else {
      cards.push(baseCard);
      cardMap[String(v.id)] = baseCard;
    }
  }

  const result = [];

  for (const group of groups.sort((a, b) => a.sort - b.sort)) {
    const groupedCards = [];

    for (const cardId of group.subVoucherIds) {
      const card = cardMap[String(cardId)];
      if (card) {
        groupedCards.push(card);
        delete cardMap[String(cardId)];
      }
    }

    group.cards = groupedCards.sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0));
    result.push(group);
  }

  const ungrouped = Object.values(cardMap).sort(
    (a, b) => (a.sort ?? 0) - (b.sort ?? 0)
  );

  result.push(...ungrouped);

  return result;
};

export const renderOptionalVoucherBadge = (intl: any) => {
  return (
    <Tooltip title={intl.formatMessage({ id: 'optional-voucher-tooltip' })}>
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
          height: '20px',
          padding: '0 6px',
          border: '1px solid #ffd591',
          borderRadius: '4px',
          background: '#fff7e6',
          color: '#ad6800',
          fontSize: '11px',
          fontWeight: 500,
          lineHeight: '18px',
          whiteSpace: 'nowrap',
          cursor: 'help',
        }}>
        <IconButton
          icon={'info-circle'}
          iconStyle={'far'}
          backgroundOff
          customStyle={{
            fontSize: '11px',
            color: '#ad6800',
          }}
          containerStyle={{
            maxHeight: '12px',
            maxWidth: '12px',
            minHeight: '12px',
            minWidth: '12px',
          }}
        />

        <span>{intl.formatMessage({ id: 'optional' })}</span>
      </span>
    </Tooltip>
  );
};
